import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import AddressSettingsItem from "components/AddressSettingsItem";
import styles from "./AddressSettingsList.module.css";
import AddressModal from "components/AddressModal";
import ValidateAddressModal from "components/ValidateAddressModal";
import { useApiErrorMessage } from "hooks/useApiErrorMessage";
import { useTranslation } from "react-i18next";
import {
  useDeleteAddress,
  useUpdateAddress,
  useValidateAddress,
} from "../../hooks/useAddresses";

const getAddressKey = (address, index = 0) =>
  String(
    address?.addressId ??
      address?.iboAddressId ??
      address?.id ??
      `${address?.address1 ?? "address"}-${address?.zip ?? ""}-${index}`
  );

const isAddressPrimary = (address) =>
  Boolean(address?.isPrimary) || Number(address?.primary ?? 0) === 1;

const isAddressDefault = (address) =>
  Boolean(address?.isDefault) || Number(address?.shipping ?? 0) === 1;

const sortByPrimaryFirst = (list = []) =>
  [...list].sort(
    (a, b) => (isAddressPrimary(b) ? 1 : 0) - (isAddressPrimary(a) ? 1 : 0)
  );

const toSettingsAddresses = (list = []) =>
  sortByPrimaryFirst(
    list.map((address, i) => ({
      ...address,
      _addressKey: getAddressKey(address, i),
    }))
  );

export function AddressSettingsList({
  addressList,
  onAddressUpdate,
  onPrimaryChange,
}) {
  const { t } = useTranslation();
  const updateAddress = useUpdateAddress();
  const deleteAddress = useDeleteAddress();
  const validateAddress = useValidateAddress();
  const [localAddressList, setLocalAddressList] = useState(
    toSettingsAddresses(addressList)
  );
  const [selectedPrimaryKey, setSelectedPrimaryKey] = useState(null);
  const [selectedDefaultKey, setSelectedDefaultKey] = useState(null);
  const [currentAddressForEdit, setCurrentAddressForEdit] = useState(null);
  const [editingAddressKey, setEditingAddressKey] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [suggestedAddress, setSuggestedAddress] = useState(null);
  const [validationError, setValidationError] = useState("");
  const apiErrorMessage = useApiErrorMessage();

  useEffect(() => {
    setLocalAddressList(toSettingsAddresses(addressList));
  }, [addressList]);

  useEffect(() => {
    if (!localAddressList.length) {
      setSelectedPrimaryKey(null);
      setSelectedDefaultKey(null);
      return;
    }

    const primaryAddress = localAddressList.find(isAddressPrimary);
    const primaryKey =
      primaryAddress?._addressKey ?? localAddressList[0]._addressKey;

    const defaultAddress = localAddressList.find(isAddressDefault);
    const defaultKey =
      defaultAddress?._addressKey ?? localAddressList[0]._addressKey;

    setSelectedPrimaryKey(primaryKey);
    setSelectedDefaultKey((prev) => (prev === null ? defaultKey : prev));
  }, [localAddressList]);

  useEffect(() => {
    const selectedKey = selectedDefaultKey ?? selectedPrimaryKey;
    if (selectedKey === null) return;
    const selectedAddress = localAddressList.find(
      (address) => address._addressKey === selectedKey
    );
    if (!selectedAddress) return;
    onPrimaryChange?.(selectedAddress);
  }, [localAddressList, selectedPrimaryKey, selectedDefaultKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateAddressesAndNotifyParent = (newAddressList) => {
    const sorted = sortByPrimaryFirst(newAddressList);
    setLocalAddressList(sorted);
    onAddressUpdate(sorted);
  };

  const getAddressId = (address) =>
    address?.addressId ?? address?.iboAddressId ?? address?.id;

  const handleTogglePrimary = (targetKey) => {
    if (selectedPrimaryKey === targetKey) return;

    const nextPrimary = localAddressList.find(
      (address) => address._addressKey === targetKey
    );
    if (!nextPrimary) return;

    // Only flip the local flags here; the actual primary-address PUT is
    // deferred until the user hits Save (see ShippingAddressModal).
    const updatedList = localAddressList.map((address) => {
      if (address._addressKey === targetKey) {
        return {
          ...address,
          primary: 1,
          isPrimary: true,
          bitwiseType: 1,
        };
      }

      if (address._addressKey === selectedPrimaryKey) {
        return {
          ...address,
          primary: 0,
          isPrimary: false,
          bitwiseType: 0,
        };
      }

      return address;
    });

    setSelectedPrimaryKey(targetKey);
    updateAddressesAndNotifyParent(updatedList);
  };

  const handleSetDefault = (targetKey) => {
    setSelectedDefaultKey(targetKey);
  };

  const handleDelete = (targetKey) => {
    const addressToDelete = localAddressList.find(
      (address) => address._addressKey === targetKey
    );
    if (!addressToDelete) return;

    if (isAddressPrimary(addressToDelete)) {
      // Temporary UX guard: primary addresses cannot be deleted per CRM rule.
      alert("You can't delete an address that is set as primary.");
      return;
    }

    const addressId = getAddressId(addressToDelete);

    const updatedList = localAddressList.filter(
      (address) => address._addressKey !== targetKey
    );

    if (selectedPrimaryKey === targetKey) {
      setSelectedPrimaryKey(updatedList[0]?._addressKey ?? null);
    }
    if (selectedDefaultKey === targetKey) {
      setSelectedDefaultKey(updatedList[0]?._addressKey ?? null);
    }

    if (addressId) {
      deleteAddress.mutate(addressId);
    }

    updateAddressesAndNotifyParent(updatedList);
  };

  const handleEdit = (address, targetKey) => {
    setValidationError("");
    setCurrentAddressForEdit(address);
    setEditingAddressKey(targetKey);
    setShowAddressModal(true);
  };

  const handleAddressModalValidate = (addressData) => {
    setCurrentAddressForEdit(addressData);
    setValidationError("");
    validateAddress.mutate(addressData, {
      onSuccess: (suggested) => {
        setSuggestedAddress(suggested ?? addressData);
        setShowAddressModal(false);
        setShowValidateModal(true);
      },
      onError: (err) => {
        setValidationError(
          apiErrorMessage(
            err,
            t(
              "errorModal.validateAddress",
              "We could not validate your address. Please check it and try again."
            )
          )
        );
      },
    });
  };

  const handleEditOriginal = () => {
    setShowValidateModal(false);
    setShowAddressModal(true);
  };

  const handleUpdateAddress = (addressChoice) => {
    if (editingAddressKey !== null) {
      const existingAddress =
        localAddressList.find(
          (address) => address._addressKey === editingAddressKey
        ) ?? {};
      const addressId = getAddressId(existingAddress);

      const updatedList = localAddressList.map((address) =>
        address._addressKey === editingAddressKey
          ? { ...existingAddress, ...addressChoice }
          : address
      );

      if (addressId) {
        updateAddress.mutate({
          addressId,
          ...existingAddress,
          ...addressChoice,
        });
      }

      updateAddressesAndNotifyParent(updatedList);
    }
    resetModalState();
  };

  const resetModalState = () => {
    setShowAddressModal(false);
    setShowValidateModal(false);
    setValidationError("");
    setCurrentAddressForEdit(null);
    setEditingAddressKey(null);
    setSuggestedAddress(null);
  };

  return (
    <div className={styles.container}>
      {localAddressList.map((address) => (
        <motion.div
          key={address._addressKey}
          layout="position"
          transition={{ duration: 0.35, ease: "easeInOut" }}
          className={styles.itemContainer}
        >
          <AddressSettingsItem
            addressInfo={address}
            isDefault={selectedDefaultKey === address._addressKey}
            isPrimary={selectedPrimaryKey === address._addressKey}
            onSetDefault={() => handleSetDefault(address._addressKey)}
            onTogglePrimary={() => handleTogglePrimary(address._addressKey)}
            onEdit={() => handleEdit(address, address._addressKey)}
            onDelete={() => handleDelete(address._addressKey)}
          />
        </motion.div>
      ))}

      <AddressModal
        address={currentAddressForEdit}
        isOpen={showAddressModal}
        onClose={resetModalState}
        onValidate={handleAddressModalValidate}
        errorMessage={validationError}
        title={t("Edit Address")}
      />

      <ValidateAddressModal
        addressInfo={currentAddressForEdit}
        suggestedAddress={suggestedAddress}
        isOpen={showValidateModal}
        onClose={() => resetModalState()}
        onUseSuggested={() => handleUpdateAddress(suggestedAddress)}
        onUseOriginal={() => handleUpdateAddress(currentAddressForEdit)}
        onEditOriginal={handleEditOriginal}
      />
    </div>
  );
}

AddressSettingsList.propTypes = {
  addressList: PropTypes.arrayOf(
    PropTypes.shape({
      country: PropTypes.string,
      address1: PropTypes.string,
      address2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string,
    })
  ),
  onPrimaryChange: PropTypes.func,
};
